/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import "./BackgroundPart.css";
import { scrollToSection } from "../navbar/Navbar";
import AnimationOverlay from "../animationOverlay/AnimationOverlay";
import { setAnimationState } from "../navbar/Navbar";
import { getCustomElementUrl } from "../../utils/cloudinary";

// Local background images
import spacex_wallpaper_1 from "../../assets/backgrounds/spacex_wallpaper_1.jpeg";
import spacex_wallpaper_2 from "../../assets/backgrounds/spacex_wallpaper_2.jpg";
import spacex_wallpaper_3 from "../../assets/backgrounds/spacex_wallpaper_3.jpeg";
import spacex_wallpaper_4 from "../../assets/backgrounds/spacex_wallpaper_4.jpeg";
import tesla_model_y_wallpaper_1 from "../../assets/backgrounds/tesla_model_y_wallpaper_1.webp";
import tesla_model_3_wallpaper_1 from "../../assets/backgrounds/tesla_model_3_wallpaper_1.jpeg";
import tesla_model_x_wallpaper_1 from "../../assets/backgrounds/tesla_model_x_wallpaper_1.jpg";
import tesla_roadster_wallpaper_1 from "../../assets/backgrounds/tesla_roadster_wallpaper_1.jpg";
import cybertruck_wallpaper_1 from "../../assets/backgrounds/cybertruck_wallpaper_1.jpeg";
import cybertruck_wallpaper_2 from "../../assets/backgrounds/cybertruck_wallpaper_2.jpg";
import cybercab_wallpaper_1 from "../../assets/backgrounds/cybercab_wallpaper_1.jpg";
import cybercab_wallpaper_2 from "../../assets/backgrounds/cybercab_wallpaper_2.jpg";
import tesla_optimus_wallpaper_1 from "../../assets/backgrounds/tesla_optimus_wallpaper_1.jpg";
import f35_wallpaper_1 from "../../assets/backgrounds/f35_wallpaper_1.jpg";
import f22_raptor_wallpaper_1 from "../../assets/backgrounds/f22_raptor_wallpaper_1.jpg";
import lamborghini_aventador_wallpaper_1 from "../../assets/backgrounds/lamborghini_aventador_wallpaper_1.jpg";
import norway_wallpaper_2 from "../../assets/backgrounds/norway_wallpaper_2.jpg";
import helicopter_wallpaper_1 from "../../assets/backgrounds/helicopter_wallpaper_1.jpg";
import a10_thunderbolt_wallpaper from "../../assets/backgrounds/a10_thunderbolt_wallpaper.webp";
import anduril_wallpaper_1 from "../../assets/backgrounds/anduril_wallpaper_1.webp";
import bugatti_chiron_1 from "../../assets/backgrounds/bugatti_chiron_1.jpg";
import bugatti_chiron_2 from "../../assets/backgrounds/bugatti_chiron_2.jpg";
import bugatti_chiron_3 from "../../assets/backgrounds/bugatti_chiron_3.jpg";
import bugatti_chiron_4 from "../../assets/backgrounds/bugatti_chiron_4.jpg";
import bugatti_chiron_5 from "../../assets/backgrounds/bugatti_chiron_5.jpg";
import mclaren_p1_wallpaper_1 from "../../assets/backgrounds/mclaren_p1_wallpaper_1.jpg";
import pagani_1 from "../../assets/backgrounds/pagani_1.jpg";
import pagani_2 from "../../assets/backgrounds/pagani_2.jpg";
import pagani_3 from "../../assets/backgrounds/pagani_3.jpg";
import rimac_2 from "../../assets/backgrounds/rimac_2.webp";
import rimac_4 from "../../assets/backgrounds/rimac_4.jpg";
import rimac_5 from "../../assets/backgrounds/rimac_5.jpg";
import rimac_6 from "../../assets/backgrounds/rimac_6.jpg";
import rimac_7 from "../../assets/backgrounds/rimac_7.jpg";
import koenigsegg_2 from "../../assets/backgrounds/koenigsegg_2.jpg";
import koenigsegg_3 from "../../assets/backgrounds/koenigsegg_3.jpg";

// Cloudinary URL for arrow icons
const whiteArrow = getCustomElementUrl('white_arrow_to_be_used_on_buttons');

// Background images array
const backgrounds = [
    spacex_wallpaper_1,
    spacex_wallpaper_2,
    spacex_wallpaper_3,
    spacex_wallpaper_4,
    tesla_model_y_wallpaper_1,
    tesla_model_3_wallpaper_1,
    tesla_model_x_wallpaper_1,
    tesla_roadster_wallpaper_1,
    cybertruck_wallpaper_1,
    cybertruck_wallpaper_2,
    cybercab_wallpaper_1,
    cybercab_wallpaper_2,
    tesla_optimus_wallpaper_1,
    f35_wallpaper_1,
    f22_raptor_wallpaper_1,
    lamborghini_aventador_wallpaper_1,
    norway_wallpaper_2,
    helicopter_wallpaper_1,
    a10_thunderbolt_wallpaper,
    anduril_wallpaper_1,
    bugatti_chiron_1,
    bugatti_chiron_2,
    bugatti_chiron_3,
    bugatti_chiron_4,
    bugatti_chiron_5,
    mclaren_p1_wallpaper_1,
    pagani_1,
    pagani_2,
    pagani_3,
    rimac_2,
    rimac_4,
    rimac_5,
    rimac_6,
    rimac_7,
    koenigsegg_2,
    koenigsegg_3,
    koenigsegg_2, // duplicate as in original
    pagani_1, // duplicate as in original
];

// Mobile-optimized backgrounds
const backgroundsMobile = [
    spacex_wallpaper_2,
    spacex_wallpaper_3,
    tesla_model_3_wallpaper_1,
    tesla_model_x_wallpaper_1,
    cybercab_wallpaper_1,
    tesla_optimus_wallpaper_1,
    f35_wallpaper_1,
    f22_raptor_wallpaper_1,
    lamborghini_aventador_wallpaper_1,
    norway_wallpaper_2,
    helicopter_wallpaper_1,
    bugatti_chiron_1,
    bugatti_chiron_3,
    bugatti_chiron_5,
    pagani_1,
];

const DEFAULT_BACKGROUND = cybercab_wallpaper_1;

interface BackgroundPartProps {
    skipAnimation?: boolean;
}

const BackgroundPart: React.FC<BackgroundPartProps> = ({
    skipAnimation = false,
}) => {
    const [currentBackground, setCurrentBackground] = useState(DEFAULT_BACKGROUND);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [showAnimation, setShowAnimation] = useState(!skipAnimation);
    const [typedText, setTypedText] = useState("");
    const [isTyping, setIsTyping] = useState(false);
    const [startBackgroundCycle, setStartBackgroundCycle] =
        useState(skipAnimation);
    const fullName = "Jens Martin Norheim Berget";
    const typingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const nameSpanRef = useRef<HTMLSpanElement>(null);

    // Add/remove animation-active class to document body
    useEffect(() => {
        if (showAnimation) {
            document.body.classList.add("animation-active");
        } else {
            document.body.classList.remove("animation-active");
        }

        return () => {
            document.body.classList.remove("animation-active");
        };
    }, [showAnimation]);

    // Start typing animation immediately if skipping the animation overlay
    useEffect(() => {
        if (skipAnimation) {
            // Small delay to ensure component is fully mounted
            setTimeout(() => {
                startTypingAnimation();
                setStartBackgroundCycle(true);
                setAnimationState(false);
            }, 100);
        }
    }, []);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useEffect(() => {
        // If animation is skipped or background cycle should start
        if (skipAnimation || startBackgroundCycle) {
            // Choose the appropriate background list based on screen size
            const activeBackgrounds = isMobile ? backgroundsMobile : backgrounds;

            let isMounted = true;
            let timerId: NodeJS.Timeout | null = null;
            let prevIndex = -1;
            let firstTime = true;

            const preloadImage = (src: string): Promise<string> => {
                return new Promise((resolve, reject) => {
                    const img = new Image();
                    img.src = src;
                    img.onload = () => resolve(src);
                    img.onerror = reject;
                });
            };

            const scheduleNextBackground = async () => {
                // Choose a random index that is different from the previous one
                let randomIndex = Math.floor(
                    Math.random() * activeBackgrounds.length
                );
                while (randomIndex === prevIndex) {
                    randomIndex = Math.floor(
                        Math.random() * activeBackgrounds.length
                    );
                }
                prevIndex = randomIndex;

                try {
                    let nextSrc: string;
                    if (firstTime) {
                        firstTime = false;
                        nextSrc = DEFAULT_BACKGROUND;
                    } else {
                        nextSrc = await preloadImage(activeBackgrounds[randomIndex]);
                    }
                    if (isMounted) {
                        setCurrentBackground(nextSrc);

                        // Schedule the next change
                        timerId = setTimeout(() => {
                            if (isMounted) {
                                scheduleNextBackground();
                            }
                        }, 5000);
                    }
                } catch (error) {
                    console.error("Failed to load image:", error);
                    // Try another image if this one fails
                    if (isMounted) {
                        scheduleNextBackground();
                    }
                }
            };

            // Start the cycle
            scheduleNextBackground();

            // Cleanup on component unmount
            return () => {
                isMounted = false;
                if (timerId) clearTimeout(timerId);
            };
        }
    }, [isMobile, skipAnimation, startBackgroundCycle]);

    const handleAnimationComplete = () => {
        setShowAnimation(false);
        setAnimationState(false);

        // The animation overlay has already completed its fade-out
        // We can start the background cycle and typing animation immediately
        setStartBackgroundCycle(true);
        startTypingAnimation();
    };

    const startTypingAnimation = () => {
        setIsTyping(true);
        let i = 0;
        typingIntervalRef.current = setInterval(() => {
            if (i < fullName.length) {
                setTypedText(fullName.substring(0, i + 1));
                i++;
            } else {
                // Keep the cursor blinking for 3 more seconds after typing completes
                setTimeout(() => {
                    setIsTyping(false);
                    if (typingIntervalRef.current) {
                        clearInterval(typingIntervalRef.current);
                    }
                }, 3000);

                // Clear the interval but keep isTyping true for the blinking cursor
                if (typingIntervalRef.current) {
                    clearInterval(typingIntervalRef.current);
                    typingIntervalRef.current = null;
                }
            }
        }, 100); // Adjust typing speed here
    };

    // Ensure cursor is properly positioned after text changes
    useEffect(() => {
        if (nameSpanRef.current) {
            // Force a reflow to ensure proper cursor positioning on all devices
            nameSpanRef.current.style.display = "inline-block";
            nameSpanRef.current.style.maxWidth = "100%";
        }
    }, [typedText]);

    useEffect(() => {
        return () => {
            if (typingIntervalRef.current) {
                clearInterval(typingIntervalRef.current);
            }
        };
    }, []);

    return (
        <>
            {showAnimation && (
                <AnimationOverlay
                    onAnimationComplete={handleAnimationComplete}
                />
            )}
            <div
                className="background-part-container"
                style={{ backgroundImage: `url(${currentBackground})` }}
            >
                <p
                    className={`background-part-name-text ${
                        isTyping
                            ? "typing"
                            : typedText === fullName
                            ? "finished"
                            : ""
                    }`}
                >
                    <span ref={nameSpanRef}>{typedText}</span>
                </p>
                <div className="background-part-button-container">
                    <div
                        className="background-part-button-1"
                        onClick={() => scrollToSection("about")}
                    >
                        <p>
                            Get Started{" "}
                            <img
                                src={whiteArrow}
                                alt="arrow"
                                className="arrow-icon-background-part"
                            />
                        </p>
                    </div>
                    <div
                        className="background-part-button-2"
                        onClick={() => scrollToSection("contact")}
                    >
                        <p>
                            Contact Me{" "}
                            <img
                                src={whiteArrow}
                                alt="arrow"
                                className="arrow-icon-background-part"
                            />
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BackgroundPart;
